import { Menu, Transition } from "@headlessui/react";
import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { ReactComponent as ChevronDown } from "../../assets/ChevronDown.svg";
import { getAuth } from "firebase/auth";
import { updateAccessor } from "../../redux/accessor/accessor.actions";
import { useNavigate } from "react-router";

const AVATAR_COLORS = [
  "bg-grey-700",
  "bg-orange-900",
  "bg-red-900",
  "bg-yellow-900",
  "bg-green-900",
  "bg-slate-900",
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const initialsFromName = name => {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0].charAt(0).toUpperCase();
  if (parts.length === 1) return first;
  const last = parts[parts.length - 1].charAt(0).toUpperCase();
  return `${first}${last}`;
};

const colorFromName = name => {
  const seed = name || "";
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const InitialsAvatar = ({ accessor, className }) => {
  const initials = initialsFromName(accessor?.name);
  const color = useMemo(() => colorFromName(accessor?.name), [accessor?.name]);

  return (
    <span
      className={`flex items-center justify-center rounded-full text-white font-bold ${color} ${className}`}
      aria-hidden='true'>
      {initials}
    </span>
  );
};

const Avatar = ({ accessor, className }) => {
  const [imgFailed, setImgFailed] = useState(false);
  const imgUrl = typeof accessor?.imgUrl === "string" ? accessor.imgUrl.trim() : "";

  useEffect(() => {
    setImgFailed(false);
  }, [imgUrl]);

  if (imgUrl && !imgFailed) {
    return (
      <img
        className={`rounded-full object-cover ${className}`}
        src={imgUrl}
        alt=''
        onError={() => setImgFailed(true)}
      />
    );
  }

  return <InitialsAvatar accessor={accessor} className={className} />;
};

const UserMenu = ({ collapsed = false, placement = "drawer" }) => {
  const dispatch = useDispatch();
  const auth = getAuth();
  const accessor = useSelector(state => state.accessor.accessor);
  const navigate = useNavigate();

  if (!accessor) return null;

  const signOutUser = () => {
    auth.signOut();
    dispatch(updateAccessor(null));
  };

  const navigateToDevAdminPage = () => {
    navigate("/dev-admin");
  };

  const isDevAdmin = accessor.groups?.some(
    group => group.groupName === "Developer Administrators",
  );

  const isHeader = placement === "header";
  const menuPosition = isHeader
    ? "origin-top-right absolute right-0 mt-2"
    : collapsed
    ? "origin-bottom-left absolute left-full bottom-0 ml-2"
    : "origin-bottom-left absolute left-0 bottom-full mb-2";

  return (
    <Menu as='div' className='relative'>
      {isHeader ? (
        <Menu.Button className='flex text-sm rounded-full !cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-lightest'>
          <span className='sr-only'>Open user menu</span>
          <Avatar accessor={accessor} className='w-8 h-8 text-xs' />
        </Menu.Button>
      ) : (
        <Menu.Button
          title={accessor.name}
          className={`flex items-center w-full text-left !cursor-pointer hover:bg-grey-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-lightest ${
            collapsed ? "justify-center p-2" : "gap-3 px-3 py-2.5"
          }`}>
          {({ open }) => (
            <>
              <span className='sr-only'>Open user menu</span>
              <Avatar
                accessor={accessor}
                className='w-9 h-9 text-sm shrink-0'
              />
              {!collapsed && (
                <>
                  <span className='min-w-0 flex-1'>
                    <span className='block truncate text-sm font-semibold text-grey-900'>
                      {accessor.name}
                    </span>
                  </span>
                  <ChevronDown
                    className={`ml-auto w-4 h-4 shrink-0 fill-current text-grey-300 transition-transform ${
                      open ? "" : "rotate-180"
                    }`}
                    aria-hidden='true'
                  />
                </>
              )}
            </>
          )}
        </Menu.Button>
      )}
      <Transition
        as={Fragment}
        enter='transition ease-out duration-100'
        enterFrom='transform opacity-0 scale-95'
        enterTo='transform opacity-100 scale-100'
        leave='transition ease-in duration-75'
        leaveFrom='transform opacity-100 scale-100'
        leaveTo='transform opacity-0 scale-95'>
        <Menu.Items
          className={`${menuPosition} z-50 w-52 rounded-lg shadow-lg p-2 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none`}>
          <div className='flex flex-col gap-2'>
            {isDevAdmin && (
              <Menu.Item>
                {({ active }) => (
                  <button
                    type='button'
                    onClick={navigateToDevAdminPage}
                    className={classNames(
                      "w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg !cursor-pointer bg-white text-grey-700 border border-grey-200 hover:border-primary-light hover:text-primary-main focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-lightest",
                      active ? "border-primary-light text-primary-main" : "",
                    )}>
                    <svg
                      width='14'
                      height='14'
                      viewBox='0 0 14 14'
                      fill='none'
                      aria-hidden='true'>
                      <circle
                        cx='7'
                        cy='7'
                        r='2'
                        stroke='currentColor'
                        strokeWidth='1.3'
                      />
                      <path
                        d='M7 2.5v1.3M7 10.2v1.3M2.5 7h1.3M10.2 7h1.3M3.9 3.9l.9.9M9.2 9.2l.9.9M3.9 10.1l.9-.9M9.2 4.8l.9-.9'
                        stroke='currentColor'
                        strokeWidth='1.3'
                        strokeLinecap='round'
                      />
                    </svg>
                    Internal Admins
                  </button>
                )}
              </Menu.Item>
            )}
            <Menu.Item>
              {({ active }) => (
                <button
                  type='button'
                  onClick={signOutUser}
                  className={classNames(
                    "w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg !cursor-pointer bg-danger-dark text-white hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-danger-light",
                    active ? "opacity-90" : "",
                  )}>
                  <svg
                    width='14'
                    height='14'
                    viewBox='0 0 14 14'
                    fill='none'
                    aria-hidden='true'>
                    <path
                      d='M6 3H3.5C3.2 3 3 3.2 3 3.5v7c0 .3.2.5.5.5H6M8.5 9.5L11 7 8.5 4.5M11 7H5.5'
                      stroke='currentColor'
                      strokeWidth='1.3'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                  Log out
                </button>
              )}
            </Menu.Item>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

export default UserMenu;
